"use client";

import React from "react";
import { Heading } from "@/components/ui/typography";
import Image from "next/image";

export default function ComplianceSupport({ heading = "Compliance Support That Scales With Your Business" }: { heading?: string } = {}) {
  return (
    <section className="w-full bg-black  pt-[120px] pb-[60px] lg:pt-[180px] lg:pb-[120px] px-4 min-[1340px]:px-0">
      <div className="relative max-w-[1200px] mx-auto flex flex-col lg:flex-row items-center justify-between max-sm:gap-4 gap-10">
        {/* IMAGE BLOCK (NOW RESPONSIVE + ALWAYS VISIBLE) */}
        <div className="w-full lg:flex-1 relative h-[250px] sm:h-[460px] md:h-[520px] lg:h-[556px] max-sm:rounded-[16px] rounded-[30px] ">
          <Image
            src="/corporate-secretary/corporate-hero.webp"
            alt="Hero Visual"
            fill
            className="object-cover rounded-[30px]"
            priority
          />
        </div>
        <div className="block max-sm:hidden absolute right-0 top-5 lg:right-auto lg:top-auto lg:-left-[60px] lg:bottom-[-20px] w-[122px] h-[88px]">
          <Image
            src="/hero-2.webp"
            alt="decor"
            fill
            className="object-contain"
          />
        </div>

        {/* CONTENT */}
        <div className="w-full lg:flex-1 flex flex-col max-sm:gap-2 gap-8">
          {/* TITLE + TEXT */}
          <div className="flex flex-col max-sm:gap-2 gap-6">
            <Heading as="h2" variant="section">
              {heading}
            </Heading>

            <p className="text-[16.3px] font-medium sm:text-[16px] lg:text-[18px] leading-6 text-white/70 font-inter">
              Your company structure evolves as you grow. We help keep
              everything aligned properly behind the scenes.
            </p>
            <p className="font-inter font-bold text-[12px] leading-[24px] text-white/70">
              Includes support for:
            </p>
            <div className="text-left flex flex-col gap-4 text-[15px] sm:text-[16px] lg:text-[18px] leading-[24px]">
              <div className="flex items-start gap-3">
                <div className="max-sm:h-5 h-7 flex items-center justify-center">
                  <img
                    className="max-sm:h-[19px] max-sm:w-[19px]"
                    src="/company-formation/tick_mark.svg"
                    alt="Tick Mark"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <p className="font-inter text-white/70 max-sm:text-[14px] text-[16px] leading-[150%] ">
                    Director updates
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="max-sm:h-5 h-7 flex items-center justify-center">
                  <img
                    className="max-sm:h-[19px] max-sm:w-[19px]"
                    src="/company-formation/tick_mark.svg"
                    alt="Tick Mark"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <p className="font-inter text-white/70 max-sm:text-[14px] text-[16px] leading-[150%] ">
                    Company record maintenance
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="max-sm:h-5 h-7 flex items-center justify-center">
                  <img
                    className="max-sm:h-[19px] max-sm:w-[19px]"
                    src="/company-formation/tick_mark.svg"
                    alt="Tick Mark"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <p className="font-inter text-white/70 max-sm:text-[14px] text-[16px] leading-[150%] ">
                    Registered address coordination
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="max-sm:h-5 h-7 flex items-center justify-center">
                  <img
                    className="max-sm:h-[19px] max-sm:w-[19px]"
                    src="/company-formation/tick_mark.svg"
                    alt="Tick Mark"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <p className="font-inter text-white/70 max-sm:text-[14px] text-[16px] leading-[150%] ">
                    Shareholder changes
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="max-sm:h-5 h-7 flex items-center justify-center">
                  <img
                    className="max-sm:h-[19px] max-sm:w-[19px]"
                    src="/company-formation/tick_mark.svg"
                    alt="Tick Mark"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <p className="font-inter text-white/70 max-sm:text-[14px] text-[16px] leading-[150%] ">
                    Annual filing preparation coordination
                  </p>
                </div>
              </div>
            </div>
            <p className="text-white/70 text-[16px] leading-[24px] font-inter font-medium">
              Built for founders running international businesses from anywhere.
            </p>
          </div>

          {/* CTA BUTTONS */}
          <div>
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Primary */}
              <a
                href="https://calendly.com/d/cxy2-dy2-tqk/mutetaxes-consultation-call"
                target="_blank"
                rel="noopener noreferrer"
                className="flex max-sm:w-full max-sm:justify-center max-sm:py-3 items-center gap-2 w-fit bg-[#335CFF] text-white px-5 py-2.5 rounded-lg text-sm font-normal hover:opacity-90 transition"
              >
                Talk to an Expert
                <span>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M10.7965 9.99932L7.08398 6.28682L8.14448 5.22632L12.9175 9.99932L8.14448 14.7723L7.08398 13.7118L10.7965 9.99932Z"
                      fill="white"
                    />
                  </svg>
                </span>
              </a>
            </div>
          </div>
        </div>

        {/* DECOR IMAGE (ONLY DESKTOP) */}
      </div>
    </section>
  );
}
